import React from "react";
import { string, number } from "prop-types";
import { useTranslation } from "@lib/i18n";
import { priceAlgorithm } from "@lib/priceAlgorithm";

import { useIsLLDJourneySelected } from "@hooks/useIsLLDJourneySelected";
import { useIsCashJourneySelected } from "@hooks/useIsCashJourneySelected";
import { useIsLOAJourneySelected } from "@hooks/useIsLOAJourneySelected";
import { useIsVACJourneySelected } from "@hooks/useIsVACJourneySelected";
import { useIsSCFJourneySelected } from "@hooks/useIsSCFJourneySelected";
import { useIsB2BSelected } from "@hooks/useIsB2BSelected";

import { getFormattedPrice } from "@shared/v2/lib/getFormattedPrice";
import { useGetJourneySpecificConfig } from "@hooks/useGetJourneySpecificConfig";
import { useDeliveryPrice } from "@hooks/finance-data/useDeliveryPrice";
import { useCurrencySettingsSelector } from "@redux/reducers/appConfig";
import { useVehicleQuantitySelector } from "@redux/reducers/deal";

import { FG_DELIVERY_METHODS_PREFIX } from "@shared/constants";
import { twMerge } from "tailwind-merge";

const DeliveryOptionPrice = ({ deliveryId, netPrice }) => {
  const { t } = useTranslation();

  const isLLDJourneySelected = useIsLLDJourneySelected();
  const isCashJourneySelected = useIsCashJourneySelected();
  const isLOAJourneySelected = useIsLOAJourneySelected();
  const isVACJourneySelected = useIsVACJourneySelected();
  const isSCFJourneySelected = useIsSCFJourneySelected();
  const currencySettings = useCurrencySettingsSelector();
  const isB2BSelected = useIsB2BSelected();
  const { multipleVehicles } = useGetJourneySpecificConfig();
  const quantity = useVehicleQuantitySelector();
  const freeDeliveryText = t("basket.delivery.free");

  if (isLLDJourneySelected || isVACJourneySelected) {
    const isMultipleVehiclesAllowed = multipleVehicles?.enabled;

    const financeAccessoryIncrease = useDeliveryPrice(
      FG_DELIVERY_METHODS_PREFIX + deliveryId
    );

    return financeAccessoryIncrease === 0 ? (
      freeDeliveryText
    ) : (
      <span
        className={twMerge(
          "flex grow-0 shrink basis-1/4 whitespace-nowrap",
          isMultipleVehiclesAllowed &&
            isB2BSelected &&
            "flex-col items-end py-2"
        )}
      >
        {isMultipleVehiclesAllowed && (
          <>{t("mathSymbols.times", { quantity })}</>
        )}

        {getFormattedPrice(
          financeAccessoryIncrease,
          currencySettings.format,
          currencySettings.symbol
        )}

        <sup className="uppercase top-1">
          {isB2BSelected ? t("price.b2b.month") : t("price.b2c.month")}
        </sup>
      </span>
    );
  }

  if (isLOAJourneySelected || isCashJourneySelected || isSCFJourneySelected) {
    return netPrice === 0
      ? freeDeliveryText
      : getFormattedPrice(
          priceAlgorithm.times(netPrice, quantity),
          currencySettings.format,
          currencySettings.symbol
        );
  }

  return null;
};

DeliveryOptionPrice.propTypes = {
  deliveryId: string,
  netPrice: number,
};

export default DeliveryOptionPrice;
